import {
  useState,
  useEffect,
  useContext,
  SetStateAction,
  Dispatch,
} from "react";
import { Box, Menu, MenuItem, TextField } from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { styles } from "./styles";
import CustomButton from "../CustomButton/CustomButton";
import {
  ConvertFolderToProjectResponse,
  CreateProjectResponse,
  DeleteFolderResponse,
  Folder,
  // LinkFoldersToProjectResponse,
  PostFolderResponse,
} from "../../proto/api_pb";
import { toast } from "react-toastify";
import { deleteFolder, postFolder } from "../../services/folderService";
import useGetValidAccessToken from "../../hooks/useGetValidAccessToken";
import DeleteConfirmationModal from "../Modal/DeleteConfirmationModal";
import {
  addProject,
  handleConvertFolderToProject,
} from "../../services/projectService";
import { BORDER_COLOR } from "../../theme";
import {
  AuthContext,
  AuthContextInterface,
  UserDetailsContext,
} from "../../contexts";
import { useNavigate } from "react-router-dom";
import { TrackObj } from "../../pages/MyTracks/types";
import { OpenFoldersListType } from "../SingleProject/types";
import { ShareModal } from "../Modal/ShareModal";
import { ShareType } from "../../proto/api_pb";
import { DownloadFolderOption } from "./DownloadFolderOption";

const MoreOptionsMenu: React.FC<{
  folder: Folder;
  handleRefresh: () => Promise<void>;
  projects?: CreateProjectResponse[];
  userId?: string;
  enterprise?: { id: string };
  setTracks: Dispatch<SetStateAction<TrackObj[]>>;
  setOpenFoldersList:
    | Dispatch<SetStateAction<OpenFoldersListType[]>>
    | undefined;
  isCountUpdated?: boolean;
  setIsCountUpdated?: (value: boolean) => void;
}> = ({
  folder,
  handleRefresh,
  projects = [],
  setTracks,
  setOpenFoldersList,
  isCountUpdated,
  setIsCountUpdated,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [newName, setNewName] = useState(folder.getName());
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isConvertConfirmOpen, setIsConvertConfirmOpen] = useState(false);
  const open = Boolean(anchorEl);
  const { getValidAccessToken } = useGetValidAccessToken();
  const isHubPage = window.location.pathname.includes("my-tracks");
  const { userId } = useContext<AuthContextInterface>(AuthContext);
  const { enterprise } = useContext(UserDetailsContext);
  const navigate = useNavigate();

  useEffect(() => {
    setNewName(folder.getName());
  }, [folder]);

  const handleClose = () => {
    setAnchorEl(null);
    setIsEditing(false);
    setNewName(folder.getName());
  };

  const handleEditSubmit = async () => {
    if (!newName.trim()) {
      toast.error("Folder name cannot be empty");
      return;
    }
    setIsLoading(true);
    const newFolder = folder;
    newFolder.setName(newName.trim());
    const token = await getValidAccessToken();
    const res = await postFolder(token, newFolder);
    if (res instanceof PostFolderResponse) {
      toast.success("Folder updated successfully");
      await handleRefresh();
      setIsLoading(false);
      handleClose();
    } else {
      setIsLoading(false);
      toast.error("Fail to update folder");
    }
  };

  const convertFolderToProject = async (folder: Folder) => {
    const projectName = folder.getName();
    setIsLoading(true);
    const token = await getValidAccessToken();

    const res = await addProject(
      token,
      userId,
      projectName,
      "",
      [],
      "",
      enterprise?.id as string,
      []
    );

    if (res instanceof CreateProjectResponse) {
      const newProjectId = res.getProjectId();
      const token = await getValidAccessToken();
      const resp = await handleConvertFolderToProject(
        token,
        enterprise?.id ?? "",
        newProjectId,
        folder.getId()
      );
      if (resp instanceof ConvertFolderToProjectResponse) {
        console.log("Folder linked to project successfully");
        toast.success("Folder converted to project");
        setIsConvertConfirmOpen(false);
        if (setOpenFoldersList) setOpenFoldersList([]);
      }

      await handleRefresh();
      setIsLoading(false);
      handleClose();
      navigate(`/project/${projectName}/${newProjectId}`);
      setTracks([]);
    } else {
      toast.error("Something went wrong while creating the project");
      setIsLoading(false);
    }
  };

  const onDelete = async (showToast = true) => {
    setIsLoading(true);
    if (isCountUpdated && setIsCountUpdated) setIsCountUpdated(true);
    const token = await getValidAccessToken();
    const res = await deleteFolder(token, folder.getId());
    if (res instanceof DeleteFolderResponse) {
      if (showToast) {
        toast.warn("Folder deleted successfully");
        setIsLoading(false);
        setIsConfirmOpen(false);
      }
      await handleRefresh();
      handleClose();
    } else {
      console.log(res);
      toast.error("Fail to delete folder");
      setIsLoading(false);
    }
  };

  return (
    <Box>
      <Box
        id="menu-button"
        aria-controls={open ? "menu" : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        onClick={(e) => setAnchorEl(e.currentTarget)}
        sx={styles.folderMenuIcon}
      >
        <MoreVertIcon />
      </Box>
      <Menu
        id="menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{ sx: { mt: 4 } }}
        sx={styles.moreVerticalIconMenu}
      >
        {isEditing ? (
          <Box sx={styles.editFolderNameModal}>
            <TextField
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              autoFocus
              size="small"
            />
            <CustomButton
              text="Save"
              onClick={handleEditSubmit}
              loading={isLoading}
            />
          </Box>
        ) : (
          <Box
            sx={{ border: `1px solid ${BORDER_COLOR}`, borderRadius: "8px" }}
          >
            <MenuItem onClick={() => setIsEditing(true)}>
              Edit Folder Name
            </MenuItem>
            {(isHubPage ||
              window.location.pathname.includes("untitled") ||
              window.location.pathname.includes("undefined")) && (
              <MenuItem onClick={() => setIsConvertConfirmOpen(true)}>
                Create Project
              </MenuItem>
            )}
            <ShareModal
              id={folder.getId()}
              title={folder.getName()}
              shareType={ShareType.SHARE_TYPE_FOLDER}
            />
            <DownloadFolderOption folder={folder} />
            <MenuItem onClick={() => setIsConfirmOpen(true)}>
              Delete Folder
            </MenuItem>
          </Box>
        )}
      </Menu>
      <DeleteConfirmationModal
        message={"Are you sure you want to delete this folder?"}
        toggle={() => setIsConfirmOpen(!isConfirmOpen)}
        isMessageModalOpen={isConfirmOpen}
        handleConfirm={onDelete}
        loading={isLoading}
      />
      <DeleteConfirmationModal
        message={"Do you want to convert this folder into a project?"}
        toggle={() => setIsConvertConfirmOpen(!isConvertConfirmOpen)}
        isMessageModalOpen={isConvertConfirmOpen}
        handleConfirm={() => {
          convertFolderToProject(folder);
        }}
        loading={isLoading}
      />
    </Box>
  );
};

export default MoreOptionsMenu;
